/**
 * SessionContext.js - Manages user session time tracking
 * 
 * Handles:
 * - Daily time allowance calculation (using server time in EST)
 * - Countdown timer state
 * - Session update API calls on logout/expiry
 * - Midnight boundary edge cases
 */

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { API_BASE_URL, apiFetch } from '../config';

const SessionContext = createContext(null);

/**
 * Parse server datetime string and extract date (YYYY-MM-DD format)
 * @param {string} serverDateTime - Format: "2026-02-05 16:51:13"
 * @returns {string} Date in YYYY-MM-DD format
 */
const getDateFromServerDateTime = (serverDateTime) => {
  if (!serverDateTime) return null;
  // Server format: "2026-02-05 16:51:13"
  return serverDateTime.split(' ')[0]; // Returns "2026-02-05"
};

/**
 * Calculate seconds until midnight from server datetime
 * @param {string} serverDateTime - Format: "2026-02-05 16:51:13"
 * @returns {number} Seconds until midnight EST
 */
const getSecondsUntilMidnightFromServerTime = (serverDateTime) => {
  if (!serverDateTime) return 86400; // Default to 24 hours if no server time
  
  // Server format: "2026-02-05 16:51:13"
  const timePart = serverDateTime.split(' ')[1]; // "16:51:13"
  const [hours, minutes, seconds] = timePart.split(':').map(Number);
  
  // Calculate seconds elapsed today
  const secondsElapsedToday = (hours * 3600) + (minutes * 60) + seconds;
  
  // Seconds until midnight = 86400 (24 hours) - seconds elapsed
  return 86400 - secondsElapsedToday;
};

/**
 * Get next day date string from a given date
 * @param {string} dateString - Format: "2026-02-05"
 * @returns {string} Next day in YYYY-MM-DD format
 */
const getNextDay = (dateString) => {
  const date = new Date(dateString + 'T00:00:00');
  date.setDate(date.getDate() + 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get the current date in EST timezone as YYYY-MM-DD string.
 * Always uses Intl.DateTimeFormat to get the real current EST date,
 * avoiding issues where timer-based midnight detection or UTC offsets
 * could cause the date to appear as the next day.
 */
const getCurrentESTDate = () => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(new Date());
  const map = new Map(parts.map(p => [p.type, p.value]));
  return `${map.get('year')}-${map.get('month')}-${map.get('day')}`;
};

export const SessionProvider = ({ children }) => {
  // Session data from login
  const [sessionData, setSessionData] = useState(null);
  
  // Timer state
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  const [hasShownWarning, setHasShownWarning] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Loading state for initial fetch
  
  // Refs for tracking
  const hasInitializedRef = useRef(false);
  const timerIntervalRef = useRef(null);
  const onSessionExpiredCallbackRef = useRef(null);
  const sessionPointsAwardedRef = useRef(false); // Track if 20-min session points already awarded
  
  /**
   * Initialize session from login data
   * @param {Object} userData - User data from login API (includes server_date_time_est)
   */
  const initializeSession = useCallback((userData) => {
    if (!userData) return;
    
    // Skip if already initialized (e.g., restored from localStorage on refresh)
    if (hasInitializedRef.current) {
      console.log('[SessionContext] Already initialized, skipping initializeSession');
      return;
    }
    
    const allowedDaily = parseInt(userData.allowed_daily_seconds) || 0;
    const usedToday = parseInt(userData.daily_seconds_used) || 0;
    const lastUsageDate = userData.last_usage_date || null;
    const serverDateTime = userData.server_date_time_est; // e.g., "2026-02-05 16:51:13"
    const serverDateEST = getDateFromServerDateTime(serverDateTime); // e.g., "2026-02-05"
    
    let availableSeconds;
    
    // Determine if it's a new day:
    // - lastUsageDate is null (new user, never logged in before)
    // - OR server date is greater than last usage date
    // - OR server date EQUALS last usage date but daily_seconds_used is 0 (midnight reset scenario)
    const isNewDay = !lastUsageDate || serverDateEST > lastUsageDate;
    
    if (isNewDay) {
      // New day or new user - gets fresh allowance
      availableSeconds = allowedDaily;
      console.log('[SessionContext] New day detected — fresh allowance:', allowedDaily);
    } else {
      // Same day - subtract already used time
      availableSeconds = Math.max(0, allowedDaily - usedToday);
      console.log('[SessionContext] Same day — available:', availableSeconds, '(allowed:', allowedDaily, '- used:', usedToday, ')');
    }
    
    // Also check time until midnight (using server time) - cap available time at that
    const secondsUntilMidnight = getSecondsUntilMidnightFromServerTime(serverDateTime);
    availableSeconds = Math.min(availableSeconds, secondsUntilMidnight);
    
    // Store session data
    // If new day, reset used seconds to 0 (ignore whatever was in DB)
    setSessionData({
      username: userData.username,
      allowedDailySeconds: allowedDaily,
      originalUsedSeconds: isNewDay ? 0 : usedToday,
      originalLastUsageDate: lastUsageDate,
      sessionStartDate: serverDateEST,
      secondsUntilMidnightAtLogin: secondsUntilMidnight // Store for midnight crossing detection
    });
    
    setRemainingSeconds(availableSeconds);
    setIsTimerActive(allowedDaily > 0); // Only activate if there's a limit
    setShowExpiryWarning(false);
    setHasShownWarning(false);
    
    // Check if already expired (no time left at login)
    if (allowedDaily > 0 && availableSeconds <= 0) {
      setIsSessionExpired(true);
      setIsTimerActive(false);
      console.log('[SessionContext] No time remaining - session expired immediately');
      // Call the logout callback after delay to show the expired message
      setTimeout(() => {
        if (onSessionExpiredCallbackRef.current) {
          onSessionExpiredCallbackRef.current();
        }
      }, 7000); // 7 second delay to show the expired overlay
    } else {
      setIsSessionExpired(false);
    }
    
    console.log('[SessionContext] Session initialized:', {
      allowedDaily,
      usedToday,
      lastUsageDate,
      serverDateEST,
      secondsUntilMidnight,
      availableSeconds,
      timerActive: allowedDaily > 0
    });
    
    hasInitializedRef.current = true;
    setIsLoading(false);
    
    // Save to localStorage for refresh/close persistence
    // We store remainingSeconds directly - it gets updated on beforeunload
    localStorage.setItem('sessionTimerData', JSON.stringify({
      username: userData.username,
      allowedDailySeconds: allowedDaily,
      originalUsedSeconds: isNewDay ? 0 : usedToday,
      sessionStartDate: serverDateEST,
      secondsUntilMidnightAtLogin: secondsUntilMidnight,
      remainingSeconds: availableSeconds,
      sessionPointsAwarded: false
    }));
  }, []);
  
  /**
   * Restore session from localStorage (no API call needed)
   * Returns true if successfully restored, false if need to fetch from API
   * Note: On page refresh, we restore from localStorage. The sessionStartDate stored
   * is the server date from login. We trust this because it was set at login time.
   * If the user manipulates their clock and refreshes, we'll detect day change on next API call.
   */
  const restoreFromLocalStorage = useCallback(() => {
    const stored = localStorage.getItem('sessionTimerData');
    console.log('[SessionContext] restoreFromLocalStorage - stored data:', stored);
    if (!stored) {
      console.log('[SessionContext] No stored session timer data found');
      return false;
    }
    
    try {
      const data = JSON.parse(stored);
      
      console.log('[SessionContext] Parsed stored data:', data);
      
      // MIDNIGHT SAFETY CHECK: If the stored session was from a previous day,
      // discard localStorage and force a fresh API call so user gets a new day's allowance
      const currentESTDate = getCurrentESTDate();
      if (data.sessionStartDate && currentESTDate > data.sessionStartDate) {
        console.log('[SessionContext] Day has changed since session started (' + data.sessionStartDate + ' → ' + currentESTDate + ') — forcing fresh API call');
        localStorage.removeItem('sessionTimerData');
        return false; // Force API call which will detect isNewDay and give fresh allowance
      }
      
      // Use stored remaining seconds directly (timer pauses when browser closed)
      const remainingNow = data.remainingSeconds || 0;
      
      // If no daily limit, no timer needed
      if (data.allowedDailySeconds <= 0) {
        setIsTimerActive(false);
        setIsLoading(false);
        hasInitializedRef.current = true;
        return true;
      }
      
      // Check if session expired while away
      if (remainingNow <= 0) {
        setIsSessionExpired(true);
        setIsTimerActive(false);
        setIsLoading(false);
        hasInitializedRef.current = true;
        return true;
      }
      
      // Restore session state
      setSessionData({
        username: data.username,
        allowedDailySeconds: data.allowedDailySeconds,
        originalUsedSeconds: data.originalUsedSeconds,
        originalLastUsageDate: null,
        sessionStartDate: data.sessionStartDate,
        secondsUntilMidnightAtLogin: data.secondsUntilMidnightAtLogin || 86400
      });
      
      setRemainingSeconds(remainingNow);
      setIsTimerActive(true);
      setIsSessionExpired(false);
      setShowExpiryWarning(false);
      setHasShownWarning(remainingNow <= 300); // If already past 5-min mark, don't show warning again
      hasInitializedRef.current = true;
      setIsLoading(false);
      
      // Restore session points flag so refresh doesn't re-award
      if (data.sessionPointsAwarded) {
        sessionPointsAwardedRef.current = true;
      }
      
      console.log('[SessionContext] Restored from localStorage:', {
        remainingNow, sessionPointsAwarded: !!data.sessionPointsAwarded
      });
      
      return true;
    } catch (e) {
      console.error('[SessionContext] Failed to restore from localStorage:', e);
      localStorage.removeItem('sessionTimerData');
      return false;
    }
  }, []);
  
  /**
   * Fetch user data from API and restore session (only if localStorage doesn't have valid data)
   */
  const restoreSession = useCallback(async () => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const username = localStorage.getItem('username');

    if (!isLoggedIn || !username || hasInitializedRef.current) {
      setIsLoading(false);
      return;
    }

    // Try localStorage first (preserves timer across refresh)
    if (restoreFromLocalStorage()) {
      console.log('[SessionContext] Session restored from localStorage');
      return;
    }

    // No valid session data (new day or cleared storage) — force re-login
    console.log('[SessionContext] No valid session data — clearing login state');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('username');
    localStorage.removeItem('userRole');
    setIsLoading(false);
  }, [restoreFromLocalStorage]);
  
  // Restore session on mount (page refresh)
  useEffect(() => {
    restoreSession();
  }, [restoreSession]);
  
  /**
   * Calculate seconds used in current session
   * Based on: started with (allowedDaily - originalUsed), now have remainingSeconds
   */
  const getSessionSecondsUsed = useCallback(() => {
    if (!sessionData) return 0;
    // How much time we started this session with
    const startedWith = sessionData.allowedDailySeconds - sessionData.originalUsedSeconds;
    // How much we've used = what we started with - what's remaining
    return Math.max(0, startedWith - remainingSeconds);
  }, [sessionData, remainingSeconds]);
  
  /**
   * Check if midnight has been crossed since login
   * Uses the stored secondsUntilMidnightAtLogin and compares with time elapsed
   */
  const hasCrossedMidnight = useCallback(() => {
    if (!sessionData) return false;
    const startedWith = sessionData.allowedDailySeconds - sessionData.originalUsedSeconds;
    const sessionSecondsUsed = Math.max(0, startedWith - remainingSeconds);
    // If we've used more seconds than were available until midnight, we crossed midnight
    const timerBasedCrossed = sessionSecondsUsed >= sessionData.secondsUntilMidnightAtLogin;
    // Also check by comparing current EST date with session start date (more reliable)
    const dateBasedCrossed = getCurrentESTDate() > sessionData.sessionStartDate;
    return timerBasedCrossed || dateBasedCrossed;
  }, [sessionData, remainingSeconds]);
  
  /**
   * Send update via sendBeacon (for browser close - doesn't wait for response)
   * Also saves current remaining seconds to localStorage so timer pauses when browser closes
   */
  const sendUpdateBeacon = useCallback(() => {
    if (!sessionData) return;
    
    const sessionSecondsUsed = getSessionSecondsUsed();
    const crossedMidnight = hasCrossedMidnight();
    // Double-check with date comparison
    const currentESTDate = getCurrentESTDate();
    const isMidnightCrossed = crossedMidnight || (currentESTDate > sessionData.sessionStartDate);
    
    let dailySecondsUsed;
    
    if (isMidnightCrossed) {
      // Crossed midnight - reset seconds to 0
      dailySecondsUsed = 0;
    } else {
      // Same day - add session usage to original
      dailySecondsUsed = sessionData.originalUsedSeconds + sessionSecondsUsed;
    }
    
    // Always use the real current EST date (avoids UTC offset issues)
    const lastUsageDate = getCurrentESTDate();
    
    const updatePayload = {
      username: sessionData.username,
      daily_seconds_used: String(dailySecondsUsed),
      last_usage_date: lastUsageDate
    };
    
    // Use sendBeacon for reliable delivery on page close
    const blob = new Blob([JSON.stringify(updatePayload)], { type: 'application/json' });
    navigator.sendBeacon(`${API_BASE_URL}/api/update-login`, blob);
    
    // Save current remaining seconds to localStorage so timer pauses when browser closes
    // This way when user reopens, they see the same time they had when they closed
    const stored = localStorage.getItem('sessionTimerData');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        data.remainingSeconds = remainingSeconds;
        localStorage.setItem('sessionTimerData', JSON.stringify(data));
      } catch (e) {
        console.error('[SessionContext] Failed to update localStorage on close:', e);
      }
    }
    
    console.log('[SessionContext] Sent beacon update on close:', updatePayload, 'remainingSeconds saved:', remainingSeconds);
  }, [sessionData, getSessionSecondsUsed, hasCrossedMidnight, remainingSeconds]);
  
  // Handle browser/tab close - send update via beacon
  useEffect(() => {
    const handleBeforeUnload = () => {
      sendUpdateBeacon();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sendUpdateBeacon]);
  
  /**
   * Call update API to save session data
   * @param {string} reason - 'logout' or 'expired'
   */
  const updateSessionOnServer = useCallback(async (reason = 'logout') => {
    if (!sessionData) return false;
    
    const sessionSecondsUsed = getSessionSecondsUsed();
    const crossedMidnight = hasCrossedMidnight();
    // Double-check with date comparison
    const currentESTDate = getCurrentESTDate();
    const isMidnightCrossed = crossedMidnight || (currentESTDate > sessionData.sessionStartDate);
    
    let dailySecondsUsed;
    
    // Check if we crossed midnight since login
    if (isMidnightCrossed) {
      // Crossed midnight - reset seconds to 0
      dailySecondsUsed = 0;
      console.log('[SessionContext] Crossed midnight - resetting daily_seconds_used to 0 for new day:', currentESTDate);
    } else {
      // Same day - add session usage to original
      dailySecondsUsed = sessionData.originalUsedSeconds + sessionSecondsUsed;
    }
    
    // Always use the real current EST date (avoids UTC offset issues)
    const lastUsageDate = getCurrentESTDate();
    
    const updatePayload = {
      username: sessionData.username,
      daily_seconds_used: String(dailySecondsUsed),
      last_usage_date: lastUsageDate
    };
    
    console.log(`[SessionContext] Updating session (${reason}):`, updatePayload);
    
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/update-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatePayload),
      });
      
      const result = await response.text();
      const success = result === 'true' || response.ok;
      console.log('[SessionContext] Update result:', success);
      return success;
    } catch (error) {
      console.error('[SessionContext] Failed to update session:', error);
      return false;
    }
  }, [sessionData, getSessionSecondsUsed, hasCrossedMidnight]);
  
  /**
   * Handle session expiry
   * @param {number} finalRemainingSeconds - The remaining seconds at time of expiry (should be 0)
   */
  const handleSessionExpired = useCallback(async (finalRemainingSeconds = 0) => {
    setIsSessionExpired(true);
    setIsTimerActive(false);
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    
    // Calculate used time directly here since state may not have updated yet
    // When expired, remaining is 0, so used = allowedDaily - originalUsed
    let dbUpdateDone = false;
    if (sessionData) {
      const startedWith = sessionData.allowedDailySeconds - sessionData.originalUsedSeconds;
      const sessionSecondsUsed = Math.max(0, startedWith - finalRemainingSeconds);
      
      // Check if we crossed midnight (session used more time than was available until midnight)
      const crossedMidnight = sessionSecondsUsed >= sessionData.secondsUntilMidnightAtLogin;
      
      // Also check by comparing current EST date with session start date
      const currentESTDate = getCurrentESTDate();
      const isMidnightCrossed = crossedMidnight || (currentESTDate > sessionData.sessionStartDate);
      
      let dailySecondsUsed;
      
      if (isMidnightCrossed) {
        // Crossed midnight - reset seconds to 0 for the new day
        dailySecondsUsed = 0;
        console.log('[SessionContext] Midnight crossed detected on expiry — resetting daily_seconds_used to 0 for new day:', currentESTDate);
      } else {
        // Same day - add session usage to original
        dailySecondsUsed = sessionData.originalUsedSeconds + sessionSecondsUsed;
      }
      
      // Always use the real current EST date (avoids UTC offset issues)
      const lastUsageDate = currentESTDate;
      
      const updatePayload = {
        username: sessionData.username,
        daily_seconds_used: String(dailySecondsUsed),
        last_usage_date: lastUsageDate
      };
      
      console.log('[SessionContext] Updating session on expiry:', updatePayload);
      
      try {
        const response = await apiFetch(`${API_BASE_URL}/api/update-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updatePayload),
        });
        
        const result = await response.text();
        console.log('[SessionContext] Expiry update result:', result);
        dbUpdateDone = true;
      } catch (error) {
        console.error('[SessionContext] Failed to update session on expiry:', error);
        dbUpdateDone = true; // Don't block logout even on error
      }
    }
    
    // Ensure DB update completes before triggering logout
    // If the DB update was awaited above, proceed immediately; otherwise wait a short safety buffer
    const doLogout = () => {
      if (onSessionExpiredCallbackRef.current) {
        onSessionExpiredCallbackRef.current();
      }
    };
    
    // Show the expired overlay for 7 seconds, but ensure DB update finished first
    if (dbUpdateDone) {
      setTimeout(doLogout, 7000);
    } else {
      // Fallback: wait for DB update + 7 seconds display
      setTimeout(doLogout, 8000);
    }
  }, [sessionData]);
  
  /**
   * Set callback to be called when session expires (for auto-logout)
   */
  const setOnSessionExpiredCallback = useCallback((callback) => {
    onSessionExpiredCallbackRef.current = callback;
  }, []);
  
  /**
   * Clear session (call before logout)
   */
  const clearSession = useCallback(async () => {
    if (isTimerActive && sessionData) {
      await updateSessionOnServer('logout');
    }
    
    setSessionData(null);
    setRemainingSeconds(0);
    setIsTimerActive(false);
    setShowExpiryWarning(false);
    setHasShownWarning(false);
    setIsSessionExpired(false);
    hasInitializedRef.current = false; // Reset so next login can initialize
    sessionPointsAwardedRef.current = false; // Reset session points tracking
    localStorage.removeItem('sessionTimerData'); // Clear stored timer data
    
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, [isTimerActive, sessionData, updateSessionOnServer]);
  
  /**
   * Dismiss the expiry warning popup
   */
  const dismissExpiryWarning = useCallback(() => {
    setShowExpiryWarning(false);
  }, []);
  
  // Timer countdown effect
  useEffect(() => {
    if (!isTimerActive || remainingSeconds <= 0) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }
    
    timerIntervalRef.current = setInterval(() => {
      setRemainingSeconds(prev => {
        const newValue = prev - 1;
        
        // Show warning at 5 minutes (300 seconds)
        if (newValue === 300 && !hasShownWarning) {
          setShowExpiryWarning(true);
          setHasShownWarning(true);
        }
        
        // Check if 20 minutes (1200 seconds) of session time used — award session points once
        if (!sessionPointsAwardedRef.current && sessionData) {
          const startedWith = sessionData.allowedDailySeconds - sessionData.originalUsedSeconds;
          const sessionSecondsUsed = Math.max(0, startedWith - newValue);
          if (sessionSecondsUsed >= 1200) {
            sessionPointsAwardedRef.current = true;
            // Persist to localStorage so refresh doesn't re-award
            try {
              const stored = localStorage.getItem('sessionTimerData');
              if (stored) {
                const d = JSON.parse(stored);
                d.sessionPointsAwarded = true;
                localStorage.setItem('sessionTimerData', JSON.stringify(d));
              }
            } catch (e) { /* ignore */ }
            const username = sessionData.username || localStorage.getItem('username');
            if (username) {
              apiFetch(`${API_BASE_URL}/api/points/award`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, actionType: 'SESSION', subject: null, sessionMinutes: Math.floor(sessionSecondsUsed / 60) })
              }).then(r => r.ok ? r.json() : null)
                .then(d => { if (d) console.log(`🏆 Session time points awarded: +${d.pointsAwarded}`); })
                .catch(e => console.warn('🏆 Session points error:', e));
            }
          }
        }
        
        // Session expired
        if (newValue <= 0) {
          handleSessionExpired(0); // Pass 0 since session is fully expired
          return 0;
        }
        
        return newValue;
      });
    }, 1000);
    
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isTimerActive, hasShownWarning, handleSessionExpired]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);
  
  const value = {
    // State
    remainingSeconds,
    isTimerActive,
    showExpiryWarning,
    isSessionExpired,
    sessionData,
    isLoading, // Loading state for session restoration
    
    // Actions
    initializeSession,
    clearSession,
    dismissExpiryWarning,
    setOnSessionExpiredCallback,
    
    // Utilities
    getSessionSecondsUsed,
  };
  
  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export default SessionContext;
